import { Injectable } from '@angular/core';
import { RequestMethod } from '@core/enums/httpRequest/requestMethods.enum';
import { NgStorage } from '@core/enums/ngStorage/ngStorage.enum';
import { UserType } from '@core/enums/user/userType.enum';
import { LoginResponse } from '@core/interface/users/loginResponse';
import { AUTH_API_ROUTES } from '@core/routes/auth/auth.routes';
import { ApiResponse, HttpRequestService } from '../GlobalHttpRequest/httpRequest.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends HttpRequestService {
  /**
   * Logs the user in and redirects them to the correct page after login.
   * @param {object} credentials - The user's email and password.
   * @returns {Promise<LoginResponse>} - The response from the login request.
   */
  async login(credentials: { email: string; password: string }): Promise<LoginResponse> {
    const response = await this.makeHttpRequest<ApiResponse<LoginResponse>>(
      AUTH_API_ROUTES.login,
      credentials,
      RequestMethod.POST,
    );

    if (response.data.passwordValidated) {
      await this.userRedirectAfterLogin(response.data);
    }

    return response.data;
  }
  /**
   * Redirect user to stored route after login
   * @param {LoginResponse} backendResponse
   * @returns {Promise<void>}
   */
  async userRedirectAfterLogin(backendResponse: LoginResponse): Promise<void> {
    await this.setUserInfo(backendResponse);
    this._notify.success(`Bienvenido de vuelta, ${backendResponse.userName}!`);
    const redirected = await this.redirectToStoredRoute();
    if (redirected) {
      return;
    }
    const homeRouteByUserType: Partial<Record<UserType, string>> = {
      [UserType.CLIENT]: '/packages/quote',
      [UserType.PROVIDER]: '/providers/dashboard',
    };
    const homeRoute = homeRouteByUserType[backendResponse.userType as UserType] ?? '/dashboard';
    this._router.navigate([homeRoute]);
  }

  /**
   * Redirect user to stored route after login
   * @returns {Promise<boolean>} true if redirect successful, false otherwise
   */
  async redirectToStoredRoute(): Promise<boolean> {
    const redirectUrl = (await this.getStorage(NgStorage.REDIRECT_URL)) as string | null;
    if (redirectUrl) {
      await this.deleteStorage(NgStorage.REDIRECT_URL);
      await this._router.navigateByUrl(redirectUrl);
      return true;
    }
    return false;
  }

  /**
   * Sets user information in storage
   * @param {Partial<LoginResponse>} userObject - partial user object containing user information
   * @returns {Promise<void>} - promise that resolves when user information is set in storage
   */
  async setUserInfo(userObject: Partial<LoginResponse>): Promise<void> {
    this._userSessionService.isSessionInvalidated = false;
    if (userObject.token) {
      await this.setStorage(NgStorage.TOKEN, userObject.token);
    }

    if (userObject) {
      const { token, permissions, ...userWithoutToken } = userObject; //eslint-disable-line
      await this.setStorage(NgStorage.USER, userWithoutToken);
    }

    if (userObject.permissions) {
      await this.setStorage(NgStorage.USER_PERMISSIONS, userObject.permissions);
    }
  }

  /**
   * Makes a request to reset the password for the given email.
   * @param {string} email - email of the user to reset the password for
   * @returns {Promise<void>} - promise that resolves when the request is completed
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await this.makeHttpRequest<ApiResponse<{ message: string }>>(
      AUTH_API_ROUTES.requestPasswordReset,
      { email },
      RequestMethod.POST,
    );
    return response.data;
  }

  /**
   * Makes a request to set a new password for the given token.
   * @param {string} token - token of the user to set a new password for
   * @param {string} newPassword - new password of the user
   * @returns {Promise<{message: string}>} - promise that resolves with the response message when the request is completed
   */
  async setNewPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await this.makeHttpRequest<ApiResponse<{ message: string }>>(
      AUTH_API_ROUTES.setNewPassword,
      { token, password },
      RequestMethod.POST,
    );
    return response.data;
  }

  async register(payload: {
    name: string;
    email: string;
    password: string;
  }): Promise<{ message: string }> {
    const response = await this.makeHttpRequest<ApiResponse<{ message: string }>>(
      AUTH_API_ROUTES.register,
      payload,
      RequestMethod.POST,
    );
    return response.data;
  }
}
